import { ElementRef, Injectable } from '@angular/core';
import { RouteService } from './route.service';
import { RestService } from './rest.service';
import { environment } from '../../environments/environment';
import { SearchResult } from '../models/search-result';
import { ApplicationStateService } from './application-state.service';
import { PageService } from './page.service';
import _ from 'lodash';
import { CommunicationService } from './communication.service';
import { SearchHistory } from '../models/search-history';
import { QuickLinkService } from './quick-link.service';
import { OfflineDataService } from './offline-data.service';
import { Position } from '../models/position';

@Injectable({
    providedIn: 'root'
})
export class SearchService {

    private limit = 10;

    private searchResults: Array<SearchResult>;
    private textResults: Array<SearchResult>;
    private quickLinkResults: Array<SearchResult>;
    private searchPageResults: Map<number, Array<SearchResult>>;
    private searchField: ElementRef;
    private searchText = '';

    constructor(private routeService: RouteService,
                private restService: RestService,
                private pageService: PageService,
                private communicationService: CommunicationService,
                private quickLinkService: QuickLinkService,
                private applicationStateService: ApplicationStateService,
                private offlineDataService: OfflineDataService) {
        this.searchResults = [];
        this.searchPageResults = new Map<number, Array<SearchResult>>();
    }

    /**
     * Get search results for a given search text / query.
     *
     * @param {string} query
     * @returns {Array<SearchResult>}
     */
    findPageTexts(query: string): Promise<Array<SearchResult>> {
        if (this.applicationStateService.isOfflineModeEnabled) {
            return this.searchOfflineTexts(query);
        }

        const route = this.routeService.getRelativeRoute(environment.endpointUrls.pageSearch, {
            bookId: this.applicationStateService.bookId
        });

        return new Promise<Array<SearchResult>>((resolve, reject) => {
            return this.restService.get(route, { query }).then(result => {
                result = result.filter(value => this.quickLinkService.isBigEnough(value.position));
                resolve(result);

                this.searchText = query;
                this.textResults = result;
                this.fetchResults();
                this.communicationService.announceSearchChange();
            }).catch(error => {
                this.searchResults = [];

                reject(error);
            });
        });
    }

    /**
     * Get search quick link results for a given search text / query.
     * Store search results (to show globally) if requested
     *
     * @param {string} query
     * @param {boolean} store
     * @returns {Promise<Array<SearchResult>>}
     */
    findQuickLinks(query: string, store = true): Promise<Array<SearchResult>> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.quickLinkSearch, {
            bookId: this.applicationStateService.bookId
        });

        return new Promise<Array<SearchResult>>((resolve, reject) => {
            return this.restService.get(route, { query }).then(result => {
                result = result.filter(value => this.quickLinkService.isBigEnough(value.position));
                resolve(result);

                if (store) {
                    this.searchText = query;
                    this.quickLinkResults = result;
                    this.convertQuickLinks();
                    this.fetchResults();
                    this.communicationService.announceSearchChange();
                }
            }).catch(error => {
                if (store) {
                    this.searchResults = [];
                }

                reject(error);
            });
        });
    }

    /**
     * Returns Page Results of the given page (by page index)
     *
     * @param {number} pageIndex
     * @returns {Promise<Array<SearchResult>>}
     */
    getSearchResultsOfPage(pageIndex: number): Promise<Array<SearchResult>> {
        /*return new Promise(resolve => {
            let results = _.filter(this.searchResults, (result) => {
                return result.pageId === this.pageService.getPageIdByIndex(pageIndex);
            });

            resolve(results);
        });*/
        return new Promise(resolve => {
            const results = this.searchPageResults.get(this.pageService.getPageIdByIndex(pageIndex));

            resolve(results);
        });
    }

    /**
     * set active status of given result to true (all other results to false)
     *
     * @param {SearchResult} result
     */
    setActive(result: SearchResult): void {
        this.searchResults = _.map(this.searchPageResults.get(result.pageId), searchResult => {
            if (searchResult.text === result.text && searchResult.position === result.position && searchResult.pageId === result.pageId) {
                searchResult.active = true;

                return searchResult;
            }

            searchResult.active = false;

            return searchResult;
        });

        this.communicationService.announceSearchChange();
    }

    /**
     * sets active status of all results to false
     */
    clearActive(): void {
        this.searchPageResults.forEach(value => {
            _.forEach(value, result => {
                result.active = false;
            });
        });

        this.communicationService.announceSearchChange();
    }

    /**
     * deletes all results
     */
    clearResults(): void {
        this.searchResults = [];
        this.textResults = [];
        this.quickLinkResults = [];
        this.searchPageResults = new Map<number, Array<SearchResult>>();
        this.searchText = '';

        this.communicationService.announceSearchChange();
    }

    /**
     * store search text in localStorage
     *
     * @param {string} query
     */
    storeSearch(query: string): void {
        if (query.trim() === '') {
            return;
        }
        const historyEntry = new SearchHistory(query);
        let history = JSON.parse(localStorage.getItem('searchHistory'));
        if (history == null) {
            history = [];
        }

        history = history.filter(value => value.text !== historyEntry.text);

        history.unshift(historyEntry);

        while (history.length > this.getLimit()) {
            history.pop();
        }

        localStorage.setItem('searchHistory', JSON.stringify(history));
    }

    /**
     * returns the search history stored in local storage
     *
     * @returns {Array<SearchHistory>}
     */
    getSearchHistory(): Array<SearchHistory> {
        let history = JSON.parse(localStorage.getItem('searchHistory'));
        if (history == null) {
            history = [];
        }

        return history;
    }

    /**
     * opens search sidebar
     */
    startSearch(): void {
        if (this.searchField) {
            setTimeout(() => {
                $(this.searchField.nativeElement).focus().select();
            }, 300);
        }
    }

    /**
     * returns the limit for the search history
     *
     * @returns {number}
     */
    getLimit(): number {
        return this.limit;
    }

    /**
     * sets actual search field
     *
     * @param {ElementRef} searchField
     */
    setSearchField(searchField: ElementRef): void {
        this.searchField = searchField;
    }

    /**
     * get actual search field
     *
     * @returns {string}
     */
    getSearchText(): string {
        return this.searchText;
    }

    /**
     * search texts in offline mode
     *
     * @param query
     * @returns {Promise}
     */
    searchOfflineTexts(query: string): Promise<Array<SearchResult>> {
        return new Promise<Array<SearchResult>>((resolve) => {
            const pageTexts = this.offlineDataService.getData('pageTexts');

            let results: Array<SearchResult> = [];

            for (const pageText of pageTexts) {
                if (pageText.texts.original.toLowerCase().includes(query)) {
                    const searchResult = new SearchResult();
                    const position = new Position;

                    searchResult.pageId = pageText.pageId;
                    searchResult.text = pageText.texts.original;
                    searchResult.active = pageText.active;
                    position.x = pageText.position.x;
                    position.y = pageText.position.y;
                    position.w = pageText.position.w;
                    position.h = pageText.position.h;
                    searchResult.position = position;

                    results.push(searchResult);
                }
            }

            results = results.filter(value => this.quickLinkService.isBigEnough(value.position));
            resolve(results);

            this.searchText = query;
            this.textResults = results;
            this.fetchResults();
            this.communicationService.announceSearchChange();

            return results;
        });
    }

    /**
     * convert quick links in results to exercise number (cut link)
     */
    private convertQuickLinks(): void {
        this.quickLinkResults.map(result => {
            const value = result;
            value.text = this.quickLinkService.convertQuickLink(result.text);

            return value;
        });
    }

    /**
     * splits all results to their pages
     */
    private fetchResults(): void {
        this.searchPageResults = new Map<number, Array<SearchResult>>();
        this.searchResults = this.textResults.concat(this.quickLinkResults);

        _.forEach(this.searchResults, result => {
            if (!this.searchPageResults.has(result.pageId)) {
                const results = [result];
                this.searchPageResults.set(result.pageId, results);
            } else {
                this.searchPageResults.set(result.pageId, this.searchPageResults.get(result.pageId).concat([result]));
            }
        });
    }
}
